import { getOrCreateFlagsmithAnonymousUser } from '@root/utils/cookies/cookies';
import { ITraits } from 'flagsmith/types';
import type { UserTraits } from 'scribe-networking/types';

const IGNORED_FLAGSMITH_ERRORS = ['Wrong Flagsmith Configuration'];

export const isFlagError = (error: Error | string | null | undefined): boolean => {
  // TODO: The types we get from flagsmith don't include `string` so want to test that this case could still happen
  if (typeof error === 'string') {
    return !IGNORED_FLAGSMITH_ERRORS.find((ignoredError) => error.includes(ignoredError));
  }
  return !!error;
};

/**
 * Parses a query string that corresponds to a feature flag and returns an object with the field names and values that are overridden.
 *
 * This is useful when you want to force a feature flag experience for testing purposes.
 *
 * @param flagQuery The query parameter string from the URL. It should be in the format `flagName=field1:value1,field2:value2`
 *
 * @returns An object where the keys are the Feature flag's field names and the values are the field values. `'true'` and `'false'` are parsed as booleans and `'null'` is parsed as `null`.
 *
 * @example
 *  With a URL like:
 * `https://scribehow.com/signup?billing_revamp=enabled:true,pro_price:10.99`
 * and a feature flag named `billing_revamp`, it would return
 * ```javascript
 * {
 *  enabled: true,
 *  pro_price: '10.99'
 * }
 * ```
 */
export const getFlagfieldOverridesFromURLQuery = (flagQuery: string | null | undefined): Record<string, string | boolean | null> => {
  if (!flagQuery) {
    return {};
  }
  // Split the query string into key-value pairs. Fields and values are separated by a colon, and pairs are separated by commas.
  const fieldValuePairs = flagQuery.split(',').map((pair) => pair.split(':'));

  return fieldValuePairs.reduce<Record<string, string | boolean | null>>((acc, [field, value]) => {
    if (!field) return acc;

    // Parse 'true' and 'false' as booleans
    if (value?.toLowerCase() === 'true') {
      acc[field] = true;
    } else if (value?.toLowerCase() === 'false') {
      acc[field] = false;
    } else if (value?.toLowerCase() === 'null') {
      acc[field] = null;
    } else {
      acc[field] = value ?? '';
    }
    return acc;
  }, {});
};

export const getFlagsmithTraits = async ({ userTraits }: { userTraits?: UserTraits | null }) => {
  const traits: ITraits = userTraits
    ? {
        super_organization_id: userTraits.super_organization_id,
        organization_id: userTraits.organization_id,
        email_address: userTraits.email_address,
        first_name: userTraits.first_name ?? '',
        last_name: userTraits.last_name ?? '',
        date_joined: userTraits.date_joined ?? '',
        date_joined_timestamp: userTraits.date_joined_timestamp,
        super_organization_created_date: userTraits.super_organization_created_date,
        super_organization_created_date_timestamp: userTraits.super_organization_created_date_timestamp,
        plan_name: userTraits.plan_name,
        signup_source: userTraits.signup_source,
        sps_enabled: userTraits.sps_enabled,
        pro_plan_type: userTraits.pro_plan_type
      }
    : {};

  const identity = userTraits?.email_address ?? (await getOrCreateFlagsmithAnonymousUser());

  return { traits, identity };
};
